import { captureError } from '@smg-real-estate/browser-rum';
import type { ActionContext } from 'vuex';

import * as ToplistingApiService from '@/api/toplistingService';
import { getRegionalHighlightStatusForListing } from '@/helpers/regionalHighlightListingStatusLookup';
import { ApiCallStatus } from '@/store/insertions/InsertionsStore';
import type { InsertionFunnelState } from '@/store/types/insertion-funnel-store';
import type {
  RegionalHighlightAssignmentRequest,
  RegionalHighlightAssignmentResponse,
  RegionalHighlightCallbackRequest,
  RegionalHighlightCallbackResponse,
  RegionalHighlightDeactivationRequest,
  RegionalHighlightDraftAddress,
  RegionalHighlightListingStatus,
  RegionalHighlightListingStatusesByListingId,
  RegionalHighlightSlot,
  RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type RegionalHighlightOperation =
  | 'slots'
  | 'listingStatuses'
  | 'assign'
  | 'deactivate'
  | 'callback';

export interface RegionalHighlightApiStatus {
  slots: ApiCallStatus;
  listingStatuses: ApiCallStatus;
  assign: ApiCallStatus;
  deactivate: ApiCallStatus;
  callback: ApiCallStatus;
}

export type RegionalHighlightErrors = Partial<Record<RegionalHighlightOperation, unknown>>;

export interface RegionalHighlightStateFields {
  regionalHighlightSlots?: RegionalHighlightSlot[];
  regionalHighlightSlotsResponse?: RegionalHighlightSlotsResponse;
  regionalHighlightListingStatuses?: RegionalHighlightListingStatusesByListingId;
  regionalHighlightApiStatus: RegionalHighlightApiStatus;
  regionalHighlightErrors: RegionalHighlightErrors;
  regionalHighlightLastAssignment?: RegionalHighlightAssignmentResponse;
  regionalHighlightLastCallback?: RegionalHighlightCallbackResponse;
}

export const getInitialRegionalHighlightStateFields = (): RegionalHighlightStateFields => ({
  regionalHighlightSlots: undefined,
  regionalHighlightSlotsResponse: undefined,
  regionalHighlightListingStatuses: undefined,
  regionalHighlightApiStatus: {
    slots: ApiCallStatus.READY,
    listingStatuses: ApiCallStatus.READY,
    assign: ApiCallStatus.READY,
    deactivate: ApiCallStatus.READY,
    callback: ApiCallStatus.READY,
  },
  regionalHighlightErrors: {},
  regionalHighlightLastAssignment: undefined,
  regionalHighlightLastCallback: undefined,
});

const runRegionalHighlightOperation = async (
  context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
  operation: RegionalHighlightOperation,
  action: () => Promise<void>,
): Promise<boolean> => {
  context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.IN_PROGRESS });
  context.commit('clearRegionalHighlightError', operation);

  try {
    await action();
    context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.SUCCESS });
    return true;
  } catch (error) {
    context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.ERROR });
    context.commit('setRegionalHighlightError', { operation, error });
    captureError(error);
    return false;
  }
};

const refreshRegionalHighlightListingState = async (
  context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
  activeAgencyId: string,
  listingId: string,
): Promise<void> => {
  const listingIds = Array.from(
    new Set([...Object.keys(context.state.regionalHighlightListingStatuses ?? {}), listingId]),
  );

  await runRegionalHighlightOperation(context, 'slots', async () => {
    const response = await ToplistingApiService.fetchRegionalHighlightSlots(
      activeAgencyId,
      listingId,
    );
    context.commit('setRegionalHighlightSlotsResponse', response);
  });
  await runRegionalHighlightOperation(context, 'listingStatuses', async () => {
    const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
      activeAgencyId,
      { listingIds },
    );
    context.commit('setRegionalHighlightListingStatuses', statuses);
  });
};

export const regionalHighlightGetters = {
  getRegionalHighlightSlots: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightSlot[] | undefined => state.regionalHighlightSlots,
  getRegionalHighlightSlotsResponse: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightSlotsResponse | undefined => state.regionalHighlightSlotsResponse,
  getRegionalHighlightListingStatuses: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightListingStatusesByListingId | undefined =>
    state.regionalHighlightListingStatuses,
  getRegionalHighlightListingStatusByListingId:
    (state: RegionalHighlightStateFields) =>
    (listingId: string): RegionalHighlightListingStatus | undefined =>
      getRegionalHighlightStatusForListing(state.regionalHighlightListingStatuses, listingId),
  getRegionalHighlightApiStatus: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightApiStatus => state.regionalHighlightApiStatus,
  getRegionalHighlightErrors: (state: RegionalHighlightStateFields): RegionalHighlightErrors =>
    state.regionalHighlightErrors,
  getRegionalHighlightLastAssignment: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightAssignmentResponse | undefined => state.regionalHighlightLastAssignment,
  getRegionalHighlightLastCallback: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightCallbackResponse | undefined => state.regionalHighlightLastCallback,
};

export const regionalHighlightMutations = {
  setRegionalHighlightSlots(
    state: RegionalHighlightStateFields,
    slots: RegionalHighlightSlot[],
  ): void {
    state.regionalHighlightSlots = slots;
  },
  setRegionalHighlightSlotsResponse(
    state: RegionalHighlightStateFields,
    response: RegionalHighlightSlotsResponse,
  ): void {
    state.regionalHighlightSlotsResponse = response;
    state.regionalHighlightSlots = response.slots;
  },
  clearRegionalHighlightSlotsResponse(state: RegionalHighlightStateFields): void {
    state.regionalHighlightSlotsResponse = undefined;
    state.regionalHighlightSlots = undefined;
  },
  setRegionalHighlightListingStatuses(
    state: RegionalHighlightStateFields,
    statuses: RegionalHighlightListingStatusesByListingId,
  ): void {
    state.regionalHighlightListingStatuses = statuses;
  },
  setRegionalHighlightApiStatus(
    state: RegionalHighlightStateFields,
    payload: { operation: RegionalHighlightOperation; status: ApiCallStatus },
  ): void {
    state.regionalHighlightApiStatus = {
      ...state.regionalHighlightApiStatus,
      [payload.operation]: payload.status,
    };
  },
  setRegionalHighlightError(
    state: RegionalHighlightStateFields,
    payload: { operation: RegionalHighlightOperation; error: unknown },
  ): void {
    state.regionalHighlightErrors = {
      ...state.regionalHighlightErrors,
      [payload.operation]: payload.error,
    };
  },
  clearRegionalHighlightError(
    state: RegionalHighlightStateFields,
    operation: RegionalHighlightOperation,
  ): void {
    const { [operation]: _removed, ...remainingErrors } = state.regionalHighlightErrors;
    state.regionalHighlightErrors = remainingErrors;
  },
  setRegionalHighlightLastAssignment(
    state: RegionalHighlightStateFields,
    assignment: RegionalHighlightAssignmentResponse,
  ): void {
    state.regionalHighlightLastAssignment = assignment;
  },
  setRegionalHighlightLastCallback(
    state: RegionalHighlightStateFields,
    callback: RegionalHighlightCallbackResponse,
  ): void {
    state.regionalHighlightLastCallback = callback;
  },
};

export const regionalHighlightActions = {
  async fetchRegionalHighlightSlots(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    listingId?: string,
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(context, 'slots', async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlots(
        activeAgencyId,
        listingId,
      );
      context.commit('setRegionalHighlightSlotsResponse', response);
    });
  },
  async fetchRegionalHighlightSlotsForAddress(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    address: RegionalHighlightDraftAddress,
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId || !address.postalCode.trim()) {
      return;
    }

    context.commit('clearRegionalHighlightSlotsResponse');
    await runRegionalHighlightOperation(context, 'slots', async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlotsForAddress(
        activeAgencyId,
        address,
      );
      context.commit('setRegionalHighlightSlotsResponse', response);
    });
  },
  async recordRegionalHighlightPendingAssignment(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { propertyReferenceId: string; slotId: string },
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      throw new Error('Cannot record Regional Highlight intent without an active agency');
    }

    await ToplistingApiService.recordRegionalHighlightPendingAssignment(
      activeAgencyId,
      payload.propertyReferenceId,
      payload.slotId,
    );
  },
  async forgetRegionalHighlightPendingAssignment(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    propertyReferenceId: string,
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      return;
    }

    try {
      await ToplistingApiService.forgetRegionalHighlightPendingAssignment(
        activeAgencyId,
        propertyReferenceId,
      );
    } catch (error) {
      captureError(error);
    }
  },
  async fetchRegionalHighlightListingStatuses(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    listingIds: string[],
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId || listingIds.length === 0) {
      return;
    }

    await runRegionalHighlightOperation(context, 'listingStatuses', async () => {
      const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
        activeAgencyId,
        { listingIds },
      );
      context.commit('setRegionalHighlightListingStatuses', statuses);
    });
  },
  async assignRegionalHighlightSlot(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { slotId: string; assignment: RegionalHighlightAssignmentRequest },
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const assignmentSucceeded = await runRegionalHighlightOperation(context, 'assign', async () => {
      const assignment = await ToplistingApiService.assignRegionalHighlightSlot(
        activeAgencyId,
        payload.slotId,
        payload.assignment,
      );
      context.commit('setRegionalHighlightLastAssignment', assignment);
    });
    if (assignmentSucceeded) {
      await refreshRegionalHighlightListingState(
        context,
        activeAgencyId,
        payload.assignment.listingId,
      );
    }
  },
  async deactivateRegionalHighlightSlot(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { slotId: string; deactivation?: RegionalHighlightDeactivationRequest },
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const deactivationSucceeded = await runRegionalHighlightOperation(
      context,
      'deactivate',
      async () => {
        await ToplistingApiService.deactivateRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.deactivation,
        );
      },
    );
    const listingId = payload.deactivation?.listingId;
    if (deactivationSucceeded && listingId) {
      context.commit(
        'setListingActionSuccess',
        'manageAdsPage.toasts.regionalHighlightWillBeDeactivated',
      );
      await refreshRegionalHighlightListingState(context, activeAgencyId, listingId);
    }
  },
  async requestRegionalHighlightCallback(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    request: RegionalHighlightCallbackRequest,
  ): Promise<void> {
    const activeAgencyId = context.getters.getActiveAgencyId;
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(context, 'callback', async () => {
      const callback = await ToplistingApiService.requestRegionalHighlightCallback(
        activeAgencyId,
        request,
      );
      context.commit('setRegionalHighlightLastCallback', callback);
    });
  },
};
